import React, { useState } from 'react';
import {
  ShoppingBag, ExternalLink, Clock, Ruler, CheckCircle,
  ArrowLeft, Award,
} from 'lucide-react';
import { TEST_PROTOCOLS, EQUIPMENT_KIT, STORES_BY_COUNTRY } from './testProtocols';

const COLOR_CLASSES = {
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   dot: 'bg-amber-500' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    dot: 'bg-blue-500' },
  purple:  { bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200',  dot: 'bg-purple-500' },
};

function TestCard({ test, onOpen }) {
  const Icon = test.icon;
  const c = COLOR_CLASSES[test.color];
  return (
    <button
      onClick={() => onOpen(test)}
      className={`text-left bg-white rounded-2xl border ${c.border} p-5 hover:shadow-md transition flex flex-col h-full`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.bg} ${c.text}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className={`text-[10px] font-bold uppercase ${c.text}`}>{test.group}</span>
      </div>
      <h3 className="font-bold text-gray-900 mb-1">{test.label}</h3>
      {test.reference && (
        <div className="text-xs text-gray-500 mb-2">
          <span className="font-semibold">Referencia:</span> {test.reference}
        </div>
      )}
      {test.frequency && (
        <div className="text-xs text-gray-400 flex items-center gap-1 mt-auto">
          <Clock className="w-3 h-3" /> {test.frequency}
        </div>
      )}
    </button>
  );
}

function TestDetail({ test, onBack }) {
  const Icon = test.icon;
  const c = COLOR_CLASSES[test.color];
  return (
    <div className="space-y-4 max-w-3xl">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft className="w-4 h-4" /> Volver a la guía
      </button>

      <div className={`bg-white rounded-2xl border ${c.border} p-6`}>
        <div className="flex items-start gap-4">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${c.bg} ${c.text}`}>
            <Icon className="w-7 h-7" />
          </div>
          <div>
            <div className={`text-xs font-bold uppercase ${c.text}`}>{test.group}</div>
            <h2 className="text-2xl font-bold text-gray-900">{test.label}</h2>
            <div className="text-sm text-gray-500 mt-1">
              Unidad: <span className="font-semibold">{test.unit}</span> · Frecuencia: <span className="font-semibold">{test.frequency}</span>
            </div>
          </div>
        </div>
      </div>

      <div className={`bg-white rounded-2xl border ${c.border} p-6`}>
        <h3 className={`text-sm font-bold ${c.text} uppercase mb-2`}>Equipamiento</h3>
        <ul className="space-y-1 text-sm text-gray-700">
          {test.equipment.map((e, i) => (
            <li key={i} className="flex gap-2">
              <CheckCircle className={`w-4 h-4 ${c.text} flex-shrink-0 mt-0.5`} /> {e}
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="text-sm font-bold text-gray-700 uppercase mb-3">Protocolo paso a paso</h3>
        <ol className="space-y-2 text-sm text-gray-700">
          {test.instructions.map((ins, i) => (
            <li key={i} className="flex gap-3">
              <span className={`flex-shrink-0 w-6 h-6 rounded-full ${c.bg} ${c.text} flex items-center justify-center text-xs font-bold`}>
                {i + 1}
              </span>
              <span className="pt-0.5">{ins}</span>
            </li>
          ))}
        </ol>
      </div>

      {test.reference && (
        <div className={`bg-white rounded-2xl border ${c.border} p-6`}>
          <h3 className={`text-sm font-bold ${c.text} uppercase mb-1 flex items-center gap-2`}>
            <Award className="w-4 h-4" /> Referencia 8-9 años
          </h3>
          <p className="text-gray-700">{test.reference}</p>
        </div>
      )}

      {test.tip && (
        <div className="bg-gray-50 rounded-2xl p-5">
          <div className="text-sm text-gray-700">💡 <span className="font-semibold">Tip:</span> {test.tip}</div>
        </div>
      )}

      {test.timer && (
        <div className="bg-gray-900 text-white rounded-2xl p-5">
          <div className="text-xs font-bold text-emerald-400 uppercase mb-1">Timer sugerido</div>
          <div className="text-sm">{test.timer.label}: <span className="font-bold">{test.timer.seconds}s</span>{test.timer.countdown ? ' (countdown)' : ''}</div>
          <div className="text-xs text-gray-400 mt-1">Disponible en la "Evaluación completa" del jugador.</div>
        </div>
      )}
    </div>
  );
}

function EquipmentSection() {
  return (
    <div className="space-y-6">
      {/* Basic kit */}
      <div className="bg-white rounded-2xl border border-emerald-200 overflow-hidden">
        <div className="bg-emerald-50 px-5 py-4">
          <h3 className="text-lg font-bold text-emerald-900 flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" /> {EQUIPMENT_KIT.basic.title}
          </h3>
          <p className="text-sm text-emerald-700 mt-0.5">{EQUIPMENT_KIT.basic.subtitle}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Ítem</th>
                <th className="text-left px-4 py-2 font-semibold">Para qué test</th>
                <th className="text-left px-4 py-2 font-semibold">Precio ref.</th>
                <th className="text-left px-4 py-2 font-semibold">Dónde comprar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {EQUIPMENT_KIT.basic.items.map((item, i) => (
                <tr key={i}>
                  <td className="px-4 py-3 font-semibold text-gray-900">{item.name}</td>
                  <td className="px-4 py-3 text-gray-600">{item.use}</td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{item.price}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{item.where}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upgrade kit */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-5 py-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Award className="w-5 h-5" /> {EQUIPMENT_KIT.upgrade.title}
          </h3>
          <p className="text-sm text-gray-600 mt-0.5">{EQUIPMENT_KIT.upgrade.subtitle}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Ítem</th>
                <th className="text-left px-4 py-2 font-semibold">Beneficio</th>
                <th className="text-left px-4 py-2 font-semibold">Precio ref.</th>
                <th className="text-left px-4 py-2 font-semibold">Dónde</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {EQUIPMENT_KIT.upgrade.items.map((item, i) => (
                <tr key={i}>
                  <td className="px-4 py-3 font-semibold text-gray-900">{item.name}</td>
                  <td className="px-4 py-3 text-gray-600">{item.use}</td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{item.price}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{item.where}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stores by country */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <ExternalLink className="w-5 h-5 text-gray-500" /> Tiendas recomendadas
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {STORES_BY_COUNTRY.map(c => (
            <div key={c.country} className="bg-gray-50 rounded-xl p-4">
              <div className="font-bold text-gray-900 mb-2">{c.country}</div>
              <ul className="space-y-2">
                {c.stores.map((s, i) => (
                  <li key={i} className="text-sm">
                    <div className="font-semibold text-gray-800">{s.name}</div>
                    <div className="text-xs text-gray-500">{s.tag}</div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function TestsGuideTab() {
  const [selected, setSelected] = useState(null);
  const [section, setSection] = useState('tests'); // tests | equipment

  if (selected) return <TestDetail test={selected} onBack={() => setSelected(null)} />;

  // Group tests by category
  const byGroup = TEST_PROTOCOLS.reduce((acc, t) => {
    (acc[t.group] = acc[t.group] ?? []).push(t);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Tests de evaluación</h2>
        <p className="text-sm text-gray-500">12 tests validados · Protocolo paso a paso · Materiales y dónde comprarlos</p>
      </div>

      {/* Section toggle */}
      <div className="inline-flex bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setSection('tests')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${section === 'tests' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
        >
          Protocolos
        </button>
        <button
          onClick={() => setSection('equipment')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${section === 'equipment' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
        >
          Materiales y compras
        </button>
      </div>

      {section === 'tests' && (
        <div className="space-y-6">
          {Object.entries(byGroup).map(([group, tests]) => (
            <div key={group}>
              <h3 className="text-sm font-bold text-gray-600 uppercase mb-3">{group}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {tests.map(t => <TestCard key={t.type} test={t} onOpen={setSelected} />)}
              </div>
            </div>
          ))}

          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
            <div className="flex items-start gap-3">
              <Ruler className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <div className="font-bold mb-1">Sesión mensual de testeo</div>
                <p>Una sesión dedicada por mes para correr los 12 tests en orden. Mismo horario, misma superficie, misma temperatura. La <strong>consistencia</strong> del protocolo importa más que la precisión absoluta. En el perfil del jugador hay un botón <strong>"Evaluación completa"</strong> que te guía por los 12 en orden.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {section === 'equipment' && <EquipmentSection />}
    </div>
  );
}
